import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useState } from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';

import { palette } from '@/config/theme';

const brandMark = require('../../../assets/images/brand-mark.png');

/**
 * Branded launch/loading screen (premium entry). Rendered before fonts and
 * locale are ready, so it must NOT depend on AppText/useLocale - just the logo,
 * the brand gradient, and plain RN Animated (no provider needed). On native the
 * OS splash covers most of this; on web it's the entry screen.
 *
 * Composition: a brand-gradient ring frames the logo, two sonar ripples pulse
 * outward behind it, a soft glow breathes, and three dots load underneath.
 */
export function AppLoading() {
  const [pulse] = useState(() => new Animated.Value(0));
  const [ripple0] = useState(() => new Animated.Value(0));
  const [ripple1] = useState(() => new Animated.Value(0));
  const [dot0] = useState(() => new Animated.Value(0));
  const [dot1] = useState(() => new Animated.Value(0));
  const [dot2] = useState(() => new Animated.Value(0));

  useEffect(() => {
    const breathe = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 1300, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 1300, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );

    const sonar = (value: Animated.Value, delay: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(value, { toValue: 1, duration: 2200, easing: Easing.out(Easing.ease), useNativeDriver: true }),
        ]),
      );

    const bounce = (value: Animated.Value, delay: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(value, { toValue: 1, duration: 360, easing: Easing.out(Easing.ease), useNativeDriver: true }),
          Animated.timing(value, { toValue: 0, duration: 360, easing: Easing.in(Easing.ease), useNativeDriver: true }),
          Animated.delay(540 - delay),
        ]),
      );

    const animations = [
      breathe,
      sonar(ripple0, 0),
      sonar(ripple1, 1100),
      bounce(dot0, 0),
      bounce(dot1, 180),
      bounce(dot2, 360),
    ];
    animations.forEach((animation) => animation.start());
    return () => animations.forEach((animation) => animation.stop());
  }, [pulse, ripple0, ripple1, dot0, dot1, dot2]);

  const glowStyle = {
    opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.2, 0.5] }),
    transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.95, 1.15] }) }],
  };
  const logoStyle = {
    transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.035] }) }],
  };
  const rippleStyle = (value: Animated.Value) => ({
    opacity: value.interpolate({ inputRange: [0, 1], outputRange: [0.4, 0] }),
    transform: [{ scale: value.interpolate({ inputRange: [0, 1], outputRange: [0.7, 1.9] }) }],
  });
  const dotStyle = (value: Animated.Value) => ({
    opacity: value.interpolate({ inputRange: [0, 1], outputRange: [0.3, 1] }),
    transform: [{ translateY: value.interpolate({ inputRange: [0, 1], outputRange: [0, -7] }) }],
  });

  return (
    <View style={styles.root}>
      <LinearGradient
        colors={[palette.gradTop, palette.gradMid, palette.gradBottom]}
        locations={[0, 0.55, 1]}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.center}>
        <View style={styles.logoWrap}>
          <Animated.View style={[styles.ripple, rippleStyle(ripple0)]} />
          <Animated.View style={[styles.ripple, rippleStyle(ripple1)]} />
          <Animated.View style={[styles.glow, glowStyle]} />
          <Animated.View style={logoStyle}>
            {/* Brand-gradient ring framing the logo (magenta → violet → cyan). */}
            <LinearGradient
              colors={[palette.brandMagenta, palette.brandViolet, palette.brandCyan]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.ring}
            >
              <View style={styles.ringInner}>
                <Image source={brandMark} style={styles.logo} contentFit="contain" />
              </View>
            </LinearGradient>
          </Animated.View>
        </View>
        <View style={styles.dots}>
          <Animated.View style={[styles.dot, dotStyle(dot0)]} />
          <Animated.View style={[styles.dot, dotStyle(dot1)]} />
          <Animated.View style={[styles.dot, dotStyle(dot2)]} />
        </View>
      </View>
    </View>
  );
}

const RING = 138;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: palette.gradBottom },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 30 },
  logoWrap: { alignItems: 'center', justifyContent: 'center', width: 200, height: 200 },
  ripple: {
    position: 'absolute',
    width: RING,
    height: RING,
    borderRadius: 42,
    borderWidth: 1.5,
    borderColor: palette.brandCyan,
  },
  glow: {
    position: 'absolute',
    width: RING - 8,
    height: RING - 8,
    borderRadius: 44,
    backgroundColor: palette.brandCyan,
  },
  ring: {
    width: RING,
    height: RING,
    borderRadius: 42,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ringInner: {
    width: RING - 7,
    height: RING - 7,
    borderRadius: 38,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.gradBottom,
    overflow: 'hidden',
  },
  logo: { width: RING - 22, height: RING - 22, borderRadius: 32 },
  dots: { flexDirection: 'row', gap: 9 },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: palette.voltBright },
});
